import { useState, useEffect } from 'react'
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react'
import {
  getValidationSummary,
  onGenerationProgress,
  onValidationSummary,
  type GenerationProgress,
  type ValidationSummary,
} from '../services/generation-state'
import { useStudioStore } from '@/store/studio-store'
import { ValidationResultMessage } from './validation-result-message'

// ─── Types ─────────────────────────────────────────────────────

interface BlockResult {
  name: string
  status: 'pending' | 'generating' | 'validating' | 'complete' | 'error'
  score?: number
}

interface AppGenerationState {
  appName: string
  totalBlocks: number
  completedBlocks: number
  currentBlock: string
  blockResults: BlockResult[]
  overallProgress: number // 0-100
}

// ─── Status Icon ───────────────────────────────────────────────

function StatusIcon({ status }: { status: BlockResult['status'] }) {
  switch (status) {
    case 'generating':
    case 'validating':
      return <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />
    case 'complete':
      return <CheckCircle className="w-3.5 h-3.5 text-muted-foreground" />
    case 'error':
      return <XCircle className="w-3.5 h-3.5 text-destructive" />
    case 'pending':
    default:
      return <Clock className="w-3.5 h-3.5 text-muted-foreground" />
  }
}

// ─── Component ─────────────────────────────────────────────────

export function AppGenerationProgress() {
  const appName = useStudioStore((s) => s.conversation.appName)
  const [state, setState] = useState<AppGenerationState>({
    appName: appName ?? 'App',
    totalBlocks: 0,
    completedBlocks: 0,
    currentBlock: '',
    blockResults: [],
    overallProgress: 0,
  })
  const [validation, setValidation] = useState<ValidationSummary | null>(() => getValidationSummary())

  useEffect(() => {
    const unsubscribe = onValidationSummary(setValidation)
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = onGenerationProgress((progress: GenerationProgress | null) => {
      if (!progress) return

      setState((prev) => {
        const results = [...prev.blockResults]
        const existingIndex = results.findIndex((r) => r.name === progress.blockName)

        const blockStatus: BlockResult['status'] =
          progress.stage === 'fixing' ? 'validating' : progress.stage

        if (existingIndex >= 0) {
          results[existingIndex] = {
            name: progress.blockName,
            status: blockStatus,
            score: progress.score,
          }
        } else if (progress.blockName !== 'all') {
          results.push({
            name: progress.blockName,
            status: blockStatus,
            score: progress.score,
          })
        }

        const completedBlocks = results.filter((r) => r.status === 'complete').length
        const totalBlocks = Math.max(results.length, prev.totalBlocks)
        const overallProgress = totalBlocks > 0
          ? Math.round((completedBlocks / totalBlocks) * 100)
          : 0

        return {
          appName: prev.appName,
          totalBlocks,
          completedBlocks,
          currentBlock: progress.blockName,
          blockResults: results,
          overallProgress,
        }
      })
    })

    return unsubscribe
  }, [])

  // Update appName from store when it changes
  useEffect(() => {
    if (appName) {
      setState((prev) => ({ ...prev, appName }))
    }
  }, [appName])

  // Don't render if no blocks tracked AND no validation summary
  if (state.blockResults.length === 0 && !validation) return null

  return (
    <>
      {state.blockResults.length > 0 && (
        <div className="rounded-lg border bg-card p-4 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">{state.appName}</h4>
            <span className="text-xs text-muted-foreground">
              {state.completedBlocks}/{state.totalBlocks} blocks
            </span>
          </div>

          {/* Overall progress bar */}
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              data-testid="app-generation-progress-fill"
              className="h-2 bg-muted-foreground/60 rounded-full transition-all duration-300"
              style={{ width: `${state.overallProgress}%` }}
            />
          </div>

          {/* Block list */}
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {state.blockResults.map((block) => (
              <div key={block.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <StatusIcon status={block.status} />
                  <span className={block.status === 'error' ? 'text-destructive' : ''}>
                    {block.name}
                  </span>
                </div>
                {block.score != null && (
                  <span className="text-muted-foreground">{block.score}/100</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {validation && (
        <div className="mt-3" data-testid="app-generation-validation-summary">
          <ValidationResultMessage
            data={{
              averageScore: validation.averageScore,
              overallGrade: validation.overallGrade,
              blocks: validation.blocks.map((b) => ({
                name: b.name,
                score: b.score,
                grade: b.grade,
                issues: b.issues,
                enrichedFeedback: b.enrichedFeedback,
              })),
            }}
          />
        </div>
      )}
    </>
  )
}
